/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// WATCHOUT: WE HAVE A SPECIAL LOADER FOR ENDPOINTS WHICH REMOVES THE BACKEND LOGIC. ONLY THE
// METHOD AND URI ARE INCLUDED IN THIS IMPORT
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { deepEqual } from '@deckee/utilities';
import OneSignal from 'onesignal-cordova-plugin-v3';
import endpointDefinitions, {
  EndpointDefinition,
} from '../../configurations/endpoints';
import { captureEventAsync } from '../event-capture';
import { clearAll, get, set } from '../local-storage';
import { definitions, emit } from '../message-broker';
import { getStore } from '../store';
import {
  fetchMemberSettings,
  logOutReset,
} from '../store/modules/app-controls';
import { updateCurrentMember } from '../store/modules/backend-service';
import { reset as resetOldFloatplan } from '../store/modules/float-plan';
import { reset } from '../store/modules/plan';

let telemetry: any;
let clientToken: any;
let clientTokenIsValid: any;

const generateNewToken = async () => {
  clientToken = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    /* eslint-disable-next-line no-bitwise */
    const r = (Math.random() * 16) | 0;
    /* eslint-disable-next-line no-bitwise, no-mixed-operators */
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  await set('backendService', 'clientToken', clientToken);
};

export const afterLogout = async () => {
  clearAll();
  getStore().dispatch(updateCurrentMember(null));
  getStore().dispatch(logOutReset());
  getStore().dispatch(reset());
  getStore().dispatch(resetOldFloatplan());
  clientTokenIsValid = false;
  emit(definitions.authentication.stateChange, clientTokenIsValid);
  await Preferences.clear();
  await generateNewToken();
};

export class EndpointError extends Error {
  constructor(message: string, code: string) {
    super(message);
    this.name = 'EndpointError';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.code = code;
  }
}

interface DeckeeResponse {
  // success: boolean;
  data: any;
}

export const callEndpoint = async <Resp extends DeckeeResponse>(
  definition: EndpointDefinition,
  data: { [key: string]: any } = {},
  queryParams: { [key: string]: any } | null = null,
  signal: any = null,
): Promise<Resp['data']> => {
  if (!definition) {
    throw new Error(
      '[callEndpoint] called without a valid definition. Please check the callee of this function',
    );
  }
  const [method, uri] = definition;
  let body;
  let url = '';
  if (method.toLowerCase() === 'get') {
    if (queryParams) {
      url = `${uri}?${new URLSearchParams(queryParams)}`;
    } else if (data) {
      url = uri;
      Object.entries(data).forEach(([keyword, value]) => {
        url = `${url.replace(keyword, value)}`;
      });
    }
  } else {
    url = uri;

    const bodyData: { [key: string]: any } = {};

    if (data) {
      // For each body data item, check to see if there is a variable in the URL that needs to be replaced, otherwise include it in the body payload
      Object.entries(data).forEach(([keyword, value]) => {
        // See if the key exists in the URL, and that the key starts with a colon
        if (url.indexOf(keyword) > -1 && keyword[0] === ':') {
          url = `${url.replace(keyword, value)}`;
        } else {
          bodyData[keyword] = value;
        }
      });
    }

    body = JSON.stringify(bodyData);
  }

  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'api-key': clientToken,
    },
    body,
    signal,
  };

  const response = await fetch(url, options);

  // need to check response.ok and returned content type before calling .json()
  const wrapper = await response.json();

  // On 401 it means the user is not authenticated.
  if (response.status === 401) {
    afterLogout(); // clear client side auth details
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    captureEventAsync({
      type: 'app: unauthenticated api call',
      payload: {
        source: 'call-endpoint',
        url,
      },
    }).then(() => {
      // Only redirect if the URL doesn't contain login
      if (!window.location.pathname.includes('login')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.location = `/maps${window.location.search}${window.location.hash}`;
      }
    });
  }

  // eslint-disable-next-line no-warning-comments
  // TODO: Implement something here so that we can return to the callee in
  // a certain way if the request failed due to a 400 level error.
  if (!wrapper.success) {
    telemetry(
      telemetry.WARN,
      `[callEndpoint] unsuccessful request for ${definition}. Received ${JSON.stringify(
        wrapper,
      )}. Sent ${JSON.stringify(options)}`,
    );
    throw new EndpointError(
      `Unsuccessful request - ${
        wrapper && wrapper.data && wrapper.data.message
      }`,
      wrapper && wrapper.data && wrapper.data.code,
    );
  }

  return wrapper.data;
};

callEndpoint.endpoints = endpointDefinitions;

export const checkClientToken = async () => {
  const member = await callEndpoint<{ data: { id: string } }>(
    callEndpoint.endpoints.checkClientToken,
  );
  // Update the returned member in the store if it has changed
  if (!deepEqual(member, getStore().getState().backendService.currentMember)) {
    getStore().dispatch(updateCurrentMember(member));
    await set('backendService', 'member', member);

    if (member) {
      getStore().dispatch(fetchMemberSettings());
    }

    if (member && member.id && Capacitor.isNativePlatform()) {
      // @ts-ignore
      if (OneSignal?.setExternalUserId) {
        // Setting External User Id with Callback Available in SDK Version 2.9.0+

        try {
          // @ts-ignore
          OneSignal.setExternalUserId(member.id.toString(), (results: any) => {
            telemetry(
              telemetry.DEBUG,
              `[checkClientToken] Set external_user_id on OneSignal for ${
                member.id
              }, results: ${JSON.stringify(results)}`,
            );
          });
        } catch (e: any) {
          if (telemetry) {
            telemetry(
              telemetry.ERROR,
              `[checkClientToken] unable to set the external user id ${e.message}`,
            );
          }
        }
      }
    }
  }

  if ((!clientTokenIsValid && member) || (clientTokenIsValid && !member)) {
    // If there has been a change in authentication state.
    clientTokenIsValid = !!member; // we want the boolean of if member is truthy
    emit(definitions.authentication.stateChange, clientTokenIsValid);
  }
};

export const logout = async () => {
  await callEndpoint(callEndpoint.endpoints.logout);
  await afterLogout();
};

export const initialize = async ({
  telemetry: givenTelemetry,
}: {
  telemetry: any;
}) => {
  telemetry = givenTelemetry;

  clientToken = await get('backendService', 'clientToken');
  // If we don't have a clientToken, or the client token is malformed create a new one
  if (
    !clientToken ||
    !clientToken.match(
      /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/,
    )
  ) {
    await generateNewToken();
    telemetry(
      telemetry.DEBUG,
      `[callEndpoint] created new clientToken ${clientToken}`,
    );
  } else {
    telemetry(
      telemetry.DEBUG,
      `[callEndpoint] found existing clientToken ${clientToken}`,
    );
  }
};
